import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';

interface NextActionsPanelProps {
  userId?: string;
  userName?: string;
  assessmentId?: string;
}

/**
 * 下一步行动选择面板组件
 * 用于能力评估报告页面和需求评估报告页面
 */
const NextActionsPanel: React.FC<NextActionsPanelProps> = ({ userId, userName, assessmentId }) => {
  const navigate = useNavigate();
  const { switchUser, currentUser } = useUser();

  const handleOpenAssistant = async () => {
    if (!userId) return;
    if (userId !== currentUser?.id) {
      await switchUser(userId);
    }
    navigate(assessmentId ? `/ai-assistant-v2/${assessmentId}` : '/ai-assistant-v2');
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-8 border border-blue-200 shadow-lg">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-gray-800 mb-4">🤖 AI智能分析</h3>
        <p className="text-gray-600 text-lg">让AI助手为您解读评估结果，提供个性化健康建议</p>
      </div>

      <div className="flex justify-center">
        {/* 一键问AI - 仅在有用户ID时显示（评估ID可选） */}
        {userId ? (
          <button
            onClick={handleOpenAssistant}
            className="group bg-white border-2 border-purple-200 rounded-2xl p-6 sm:p-8 hover:border-purple-400 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 text-left shadow-md w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-100 to-indigo-200 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
              </div>
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
            <h5 className="font-bold text-gray-800 text-lg sm:text-xl mb-2 sm:mb-3">一键问AI 🤖</h5>
            <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed">AI智能分析您的健康风险，提供个性化建议和行动计划，让照护更简单高效</p>
            <div className="bg-purple-50 rounded-lg px-3 py-2 sm:px-4 sm:py-2 border border-purple-200">
              <span className="text-purple-700 font-semibold text-xs sm:text-sm">✨ 15项风险 · 60个预警信号</span>
            </div>
          </button>
        ) : (
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 sm:p-8 text-left shadow-md w-full max-w-md opacity-60">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
              </div>
            </div>
            <h5 className="font-bold text-gray-600 text-lg sm:text-xl mb-2 sm:mb-3">一键问AI 🤖</h5>
            <p className="text-gray-500 text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed">请先保存评估结果到用户档案，才能使用AI智能分析功能</p>
            <div className="bg-gray-50 rounded-lg px-3 py-2 sm:px-4 sm:py-2 border border-gray-200">
              <span className="text-gray-500 font-semibold text-xs sm:text-sm">💡 登录后可用</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NextActionsPanel;
